import type { ButtonHTMLAttributes } from "react";
import { cn } from "../utils/helpers";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "tertiary" | "success";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          backgroundColor: "var(--color-primary)",
          color: "var(--color-surface)",
          borderColor: "var(--neo-border-color)",
        };
      case "secondary":
        return {
          backgroundColor: "var(--color-surface)",
          color: "var(--color-text-primary)",
          borderColor: "var(--neo-border-color)",
        };
      case "tertiary":
        return {
          backgroundColor: "var(--color-tertiary)",
          color: "var(--color-surface)",
          borderColor: "var(--neo-border-color)",
        };
      case "success":
        return {
          backgroundColor: "var(--color-success)",
          color: "var(--color-surface)",
          borderColor: "var(--neo-border-color)",
        };
      default:
        return {
          backgroundColor: "var(--color-primary)",
          color: "var(--color-surface)",
          borderColor: "var(--neo-border-color)",
        };
    }
  };

  const sizeStyles = {
    sm: { padding: "0.375rem 0.75rem", fontSize: "0.875rem" },
    md: { padding: "0.625rem 1rem", fontSize: "1rem" },
    lg: { padding: "0.75rem 1.5rem", fontSize: "1.125rem" },
  };

  return (
    <button
      className={cn(
        "font-bold transition-all duration-150 cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
      style={
        {
          border: "var(--neo-border-medium)",
          boxShadow: disabled ? "none" : "var(--neo-shadow-md)",
          ...sizeStyles[size],
          ...getVariantStyles(),
        } as React.CSSProperties
      }
      disabled={disabled}
      // --- Playwright Primitives Contract (Default) ---
      data-p-type="button"
      data-p-state={disabled ? "disabled" : "enabled"}
      // ------------------------------------------------
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = "var(--neo-shadow-lg)";
          e.currentTarget.style.transform = "translate(-2px, -2px)";
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = "var(--neo-shadow-md)";
          e.currentTarget.style.transform = "translate(0, 0)";
        }
      }}
      onMouseDown={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = "var(--neo-shadow-sm)";
          e.currentTarget.style.transform = "translate(2px, 2px)";
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}
